import React from "react";
import { NotificationsNoneRounded } from "@mui/icons-material";
import type { NegotiationNotice } from "../../services/negotiationNotices";
import { FloatContainer } from "../FloatContainer";
import { formatBRL } from "../financial/InvoiceSplitPreviewModal";
import "./NegotiationNotices.css";

function positions(values: number[]) {
  return values.length < 2
    ? String(values[0])
    : `${values.slice(0, -1).join(", ")} e ${values[values.length - 1]}`;
}

export function negotiationNoticeCopy(notice: NegotiationNotice): string {
  switch (notice.type) {
    case "minor_candidate":
      return `Seu lance ainda garante visibilidade para menores nas postagens de posição ${positions(notice.positions)}. Habilite o modo nas configurações para garantir essas postagens para menores!`;
    case "minor_active":
      return `Sua empresa garante a visibilidade para menores nas postagens de posição ${positions(notice.positions)}. Se o leilão terminar agora, paga ${formatBRL(notice.totalCents)} no total (seus lances, sem taxas).`;
    case "adult_only":
      return "Sua empresa está classificada como +18 neste ciclo. Os anúncios dela circulam só para o público maior de idade.";
    case "balance_shortfall":
      return `Seu saldo está ${formatBRL(notice.shortfallCents)} abaixo do que está reservado. Recarregue antes do fim do leilão.`;
  }
}

/**
 * One bell alongside the bid amount controls, never on a post card. Notices
 * persist until the authoritative snapshot removes their condition.
 * @story ancestor #negotiationHomeContainer
 * @story args { notices: [{ type: "minor_candidate", positions: [3,7,12] }, { type: "minor_active", positions: [5,9], totalCents: 34000 }, { type: "adult_only" }, { type: "balance_shortfall", shortfallCents: 1500 }] }
 * @story state Open { defaultOpen: true }
 * @story state Empty { notices: [], defaultOpen: true }
 * @story state Stale { stale: true, defaultOpen: true }
 * @story state Pending { pending: true, notices: [], defaultOpen: true }
 */
export function NegotiationNotices({
  notices,
  stale = false,
  pending = false,
  defaultOpen = false,
}: {
  notices: NegotiationNotice[];
  stale?: boolean;
  pending?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="negotiationNotices">
      <button
        type="button"
        className="bell"
        aria-label={
          pending
            ? "Avisos da negociação: consultando"
            : `Avisos da negociação: ${notices.length} ativos`
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <NotificationsNoneRounded aria-hidden="true" />
        <span className="count" aria-hidden="true">
          {pending ? "…" : notices.length}
        </span>
      </button>
      {open && (
        <FloatContainer className="negotiationNoticesModal" close={() => setOpen(false)}>
          <div className="body">
            <h3 className="title">Avisos da negociação</h3>
            {stale && (
              <p role="status">
                Não foi possível atualizar os avisos agora. Consulte novamente em instantes.
              </p>
            )}
            {pending ? (
              <p role="status">Consultando os avisos desta negociação...</p>
            ) : notices.length === 0 ? (
              <p>Nenhum aviso ativo nesta negociação.</p>
            ) : (
              <ul className="list">
                {notices.map((notice) => (
                  <li key={notice.type}>{negotiationNoticeCopy(notice)}</li>
                ))}
              </ul>
            )}
          </div>
        </FloatContainer>
      )}
    </div>
  );
}
